import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { onAnimationEnd, tCount } from '@theme/utilities';

/**
 * The count on the header cart icon.
 *
 * Updated straight from the `cart:update` detail rather than by re-rendering
 * the header, because the header is on every page and re-rendering it to move
 * one number is disproportionate.
 *
 * The count is also mirrored into sessionStorage. Coming back to a page
 * through the back/forward cache serves the HTML as it was rendered, so
 * without this the shopper sees the count they had before adding an item.
 *
 * @extends {Component}
 */
class CartIconComponent extends Component {
  requiredRefs = ['count'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#reconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /**
   * @param {number} count
   * @param {boolean} [animate]
   */
  render = async (count, animate = true) => {
    const element = /** @type {HTMLElement} */ (this.refs.count);

    element.textContent = count > 0 ? String(count) : '';
    element.toggleAttribute('hidden', count === 0);
    this.setAttribute('aria-label', tCount('cart_count', count));

    sessionStorage.setItem('cart-count', JSON.stringify({ count, at: Date.now() }));

    if (!animate || count === 0) return;

    element.classList.add('cart-count--bump');
    await onAnimationEnd(element);
    element.classList.remove('cart-count--bump');
  };

  /** @param {Event} event */
  #onCartUpdate = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail;
    this.render(detail?.itemCount ?? detail?.cart?.item_count ?? 0);
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#reconcile();
  };

  /** Corrects a count the page was served with, if a newer one is known. */
  #reconcile() {
    const stored = sessionStorage.getItem('cart-count');
    if (!stored) return;

    try {
      const { count, at } = JSON.parse(stored);
      const rendered = Number(/** @type {HTMLElement} */ (this.refs.count).textContent || 0);

      // Only trust a very recent value. An older one is more likely to be the
      // stale side of the comparison, after a checkout in another tab.
      if (count !== rendered && Date.now() - at < 10_000) this.render(count, false);
    } catch {
      // Malformed entry from an older version of the theme.
    }
  }
}

if (!customElements.get('cart-icon-component')) {
  customElements.define('cart-icon-component', CartIconComponent);
}
